import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateTarifDto,
  GetTarifQueryDto,
  GetTarifResponseDto,
  TarifDto,
  UpdateTarifDto,
} from "@/types/api/api-types";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";

const getTarifs = async (payload?: GetTarifQueryDto) => {
  const response = await axiosApi.get<GetTarifResponseDto>(`/v1/tarif`, {
    params: payload,
  });
  return response.data;
};

export const useGetTarifs = (payload?: GetTarifQueryDto) => {
  return useQuery({
    queryKey: ["tarif", payload],
    queryFn: () => getTarifs(payload),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

const getTarif = async (id: string) => {
  const response = await axiosApi.get<TarifDto>(`/v1/tarif/${id}`);
  return response.data;
};

export const useGetTarif = (id: string) => {
  return useQuery({
    queryKey: ["tarif", id],
    queryFn: () => getTarif(id),
  });
};

const createTarif = async (payload: CreateTarifDto) => {
  const response = await axiosApi.post<TarifDto>(`/v1/tarif`, payload);
  return response.data;
};

export const useCreateTarif = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");

  return useMutation({
    mutationFn: createTarif,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tarif"] });
      toast.success(t("tarifApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("tarifApi.createError"));
    },
  });
};

const updateTarif = async ({ id, payload }: { id: string; payload: UpdateTarifDto }) => {
  const response = await axiosApi.patch<TarifDto>(`/v1/tarif/${id}`, payload);
  return response.data;
};

export const useUpdateTarif = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");

  return useMutation({
    mutationFn: updateTarif,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tarif"] });
      toast.success(t("tarifApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("tarifApi.updateError"));
    },
  });
};

const deleteTarif = async (id: string) => {
  const response = await axiosApi.delete<TarifDto>(`/v1/tarif/${id}`);
  return response.data;
};

export const useDeleteTarif = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");

  return useMutation({
    mutationFn: deleteTarif,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tarif"] });
      toast.success(t("tarifApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("tarifApi.deleteError"));
    },
  });
};
